"use client";

import React from 'react';
import { apiFetch } from '@/lib/api';

/**
 * Muted, single-active, in-view preview playback for the /videos browse grid.
 *
 * Deliberately does NOT touch the watch counter. A watch is one
 * dbo.WatchHistories row, written by POST /api/video/v2/{id}/view once the
 * detail player has accumulated min(5, 0.2 * duration) seconds of real
 * playback. Nothing in that chain can tell autoplay from a deliberate watch,
 * so a feed player calling it would inflate September's series against an
 * August baseline earned by clicks. This never calls it. Exposure gets its own
 * dbo.Events row instead, and the two series stay separate.
 */

// Exactly one card plays at a time. Module scope, because the cards are
// siblings with no common parent to hold this.
let activeId: number | null = null;
const subscribers = new Set<() => void>();

function setActive(next: number | null) {
  if (activeId === next) return;
  activeId = next;
  subscribers.forEach((fn) => {
    try { fn(); } catch { /* a stale subscriber must not stop the others */ }
  });
}

/** One exposure per card per page load, not per visit to the card. */
const exposed = new Set<number>();

/** Seconds of continuous in-view playback before an exposure counts. */
export const EXPOSURE_SECONDS = 3;

/**
 * Chosen to sit strictly BELOW the smallest watch threshold (min(5, 0.2 *
 * duration)) for any clip of 15 seconds or longer, so an exposure can never be
 * read as "a watch we chose not to count".
 */
export function environmentAllowsPreview(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return false;
    // Coarse pointer on a narrow viewport is a phone: metered data, and the
    // grid is one column so a preview fills the screen unbidden.
    const coarse = window.matchMedia?.('(pointer: coarse)')?.matches;
    if (coarse && window.innerWidth < 640) return false;
    const conn: any = (navigator as any)?.connection;
    if (conn?.saveData) return false;
    if (typeof conn?.effectiveType === 'string' && /(^|-)2g$/i.test(conn.effectiveType)) return false;
  } catch { /* a browser without matchMedia or connection just gets previews */ }
  return true;
}

type Options = {
  id: number;
  /** HLS manifest from the list endpoint. Null for guests since d130836. */
  contentUrl?: string | null;
  /** Caller's own gate, e.g. the viewer is signed in. */
  enabled: boolean;
  session?: any;
};

export function useAutoplayPreview({ id, contentUrl, enabled, session }: Options) {
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const hlsRef = React.useRef<any | null>(null);
  const playedMsRef = React.useRef(0);
  const lastTickRef = React.useRef<number | null>(null);

  // Resolved after mount: matchMedia and navigator.connection do not exist on
  // the server, and the grid's first page is server-rendered.
  const [allowed, setAllowed] = React.useState(false);
  const [isActive, setIsActive] = React.useState(false);

  const eligible = enabled && !!contentUrl && allowed;

  React.useEffect(() => { setAllowed(environmentAllowsPreview()); }, []);

  // Re-read the module-level winner whenever it changes.
  React.useEffect(() => {
    const sync = () => setIsActive(activeId === id);
    subscribers.add(sync);
    sync();
    return () => {
      subscribers.delete(sync);
      if (activeId === id) setActive(null);
    };
  }, [id]);

  // Claim or release the single active slot on scroll. useInView cannot drive
  // this: it never sets inView back to false, by design, and it is shared with
  // gallery code.
  React.useEffect(() => {
    if (!eligible) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;

    const obs = new IntersectionObserver((entries) => {
      for (const e of entries) {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          // First card to become substantially visible wins and keeps the slot
          // until it leaves, so scrolling does not thrash between neighbours.
          if (activeId === null) setActive(id);
        } else if (activeId === id) {
          setActive(null);
        }
      }
    }, { threshold: [0, 0.6] });

    obs.observe(el);
    return () => { try { obs.disconnect(); } catch { /* already gone */ } };
  }, [eligible, id]);

  const sendExposure = React.useCallback((seconds: number) => {
    if (exposed.has(id)) return;
    exposed.add(id);
    // Not postAnalytics: its debounce key is [type, id, status, code] and this
    // payload carries contentId rather than id, so every card would collapse
    // to ONE event per 60 seconds. It also beacons without the bearer, which
    // would record every exposure against "anonymous". apiFetch carries the
    // token, and the Next route forwards it.
    const body = JSON.stringify({
      events: [{
        name: 'video_autoplay_exposure',
        ts: Date.now(),
        props: { contentType: 'video', contentId: id, seconds },
      }],
    });
    const init: RequestInit = { method: 'POST', cache: 'no-store', body };
    // apiFetch reads argument 2 as the init unless it looks like a session, so
    // a null session with a third argument would silently send a GET.
    const p = session
      ? apiFetch('/api/analytics', session, init)
      : apiFetch('/api/analytics', init);
    void p.catch(() => { /* telemetry must never surface to the viewer */ });
  }, [id, session]);

  // Attach, play, and tear down. One HLS instance exists at a time because one
  // card is active at a time.
  React.useEffect(() => {
    const el = videoRef.current;
    if (!eligible || !isActive || !el || !contentUrl) return;

    let cancelled = false;
    playedMsRef.current = 0;
    lastTickRef.current = null;

    const onTimeUpdate = () => {
      const now = Date.now();
      const last = lastTickRef.current;
      lastTickRef.current = now;
      if (last == null) return;
      // Wall clock between timeupdates while playing. Paused or scrolled out
      // tears this effect down and resets the accumulator, which is what makes
      // the three seconds CONTINUOUS rather than cumulative.
      playedMsRef.current += Math.min(now - last, 1000);
      if (playedMsRef.current >= EXPOSURE_SECONDS * 1000) {
        sendExposure(EXPOSURE_SECONDS);
        el.removeEventListener('timeupdate', onTimeUpdate);
      }
    };

    el.addEventListener('timeupdate', onTimeUpdate);

    (async () => {
      try {
        const isHls = /\.m3u8(\?|$)/i.test(contentUrl);
        if (isHls && !el.canPlayType('application/vnd.apple.mpegurl')) {
          const mod: any = (await import('hls.js')).default || (await import('hls.js'));
          const Hls = mod?.default || mod;
          if (cancelled) return;
          if (Hls?.isSupported?.()) {
            // capLevelToPlayerSize keeps a card-sized preview off the 1080p
            // rendition; HLS fetches only the segments it plays, so a
            // three-second preview pulls a segment or two, not the asset.
            const hls = new Hls({ enableWorker: true, capLevelToPlayerSize: true, startLevel: 0 });
            hlsRef.current = hls;
            hls.loadSource(contentUrl);
            hls.attachMedia(el);
          } else {
            return; // no HLS support and no native playback: stay on the poster
          }
        } else {
          el.src = contentUrl;
        }
        if (cancelled) return;
        await el.play().catch(() => { /* autoplay policy said no; poster stays */ });
      } catch {
        /* a preview that cannot start is not an error the viewer should see */
      }
    })();

    return () => {
      cancelled = true;
      el.removeEventListener('timeupdate', onTimeUpdate);
      try { el.pause(); } catch { /* already detached */ }
      try {
        if (hlsRef.current?.destroy) hlsRef.current.destroy();
      } catch { /* already destroyed */ }
      hlsRef.current = null;
      try {
        el.removeAttribute('src');
        el.load();
      } catch { /* nothing buffered */ }
    };
  }, [eligible, isActive, contentUrl, sendExposure]);

  return { containerRef, videoRef, showPreview: eligible && isActive };
}

/** Test seam: module-level state outlives a component in the same page load. */
export function __resetAutoplayPreviewState() {
  activeId = null;
  subscribers.clear();
  exposed.clear();
}
